import type { ReactNode } from "react";
import { ShieldAlert } from "lucide-react";

import { useBootstrap } from "../api/queries/useBootstrap";
import { EmptyState } from "../components/common/EmptyState";
import { hasPermission, type PermissionRule } from "../domain/permissions";
import { matchRoute } from "./useRouteTitle";
import { useCurrentPath } from "./useRouteTitle";

/** The permission keys a rule names, for the refusal message. */
export function ruleKeys(rule: PermissionRule): string[] {
  if (rule === "none") return [];
  if (typeof rule === "string") return [rule];
  if ("anyOf" in rule) return [...rule.anyOf];
  return [...rule.allOf];
}

function ruleText(rule: PermissionRule): string {
  const keys = ruleKeys(rule);
  if (keys.length <= 1) return keys[0] ?? "";
  const joiner = typeof rule === "object" && "allOf" in rule ? " and " : " or ";
  return keys.join(joiner);
}

/** A full page INSIDE the shell — never a redirect home, never a 404. */
export function PermissionRefused({ rule }: { rule: PermissionRule }) {
  return (
    <main className="p-lg">
      <EmptyState
        icon={ShieldAlert}
        variant="error"
        title={`You don't have ${ruleText(rule)} on this tenant`}
        body="Ask a tenant administrator for access. Hiding a screen withholds a shortcut, never permission — the server checks every call."
      />
    </main>
  );
}

/**
 * The second guard: the route table entry's own `permission`, evaluated with the one predicate.
 * `ModuleGate` (Phase 5) stays inside it, at the page.
 */
export function PermissionRouteGuard({ children }: { children: ReactNode }) {
  const pathname = useCurrentPath();
  const { data } = useBootstrap();
  const entry = matchRoute(pathname);
  const rule: PermissionRule = entry?.permission ?? "none";
  const held = data?.user.permissions ?? [];

  if (!hasPermission(held, rule)) return <PermissionRefused rule={rule} />;
  return <>{children}</>;
}
